// ============================================================================
// GroupCarousel - swipe through groups one card at a time
// ----------------------------------------------------------------------------
// CSS scroll-snap does the sliding (touch swipe on phones, trackpad on
// desktop); the arrows page one card at a time and the dots show where you
// are. No library, no layout jank.
// ============================================================================
import React, { useEffect, useRef, useState } from 'react';
import { Box, HStack, IconButton } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import GroupCard, { GroupCardData } from './GroupCard';

interface Props {
  groups: GroupCardData[];
  onOpen: (id: string) => void;
  onJoin?: (id: string) => void;
  joiningId?: string | null;
}

const GroupCarousel: React.FC<Props> = ({ groups, onOpen, onJoin, joiningId }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth || 1;
      setIndex(Math.round(el.scrollLeft / w));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [groups.length]);

  const go = (delta: number) => {
    const el = ref.current;
    if (!el) return;
    const w = el.clientWidth;
    const next = Math.min(groups.length - 1, Math.max(0, index + delta));
    el.scrollTo({ left: next * w, behavior: 'smooth' });
  };

  if (groups.length === 0) return null;

  return (
    <Box position="relative">
      <Box ref={ref} display="flex" overflowX="auto" gap={0}
        sx={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, WebkitOverflowScrolling: 'touch' }}>
        {groups.map((g) => (
          <Box key={g.id} flex="0 0 100%" px={1} sx={{ scrollSnapAlign: 'start' }}>
            <GroupCard group={g} onOpen={onOpen} onJoin={onJoin} joining={joiningId === g.id} wide />
          </Box>
        ))}
      </Box>
      {groups.length > 1 && (
        <>
          <IconButton aria-label="Previous group" icon={<ChevronLeft size={18} />} size="sm" isRound
            position="absolute" left="-6px" top="80px" bg="rgba(10,20,32,0.85)" color="white" _hover={{ bg: 'rgba(139,92,246,0.8)' }}
            onClick={() => go(-1)} isDisabled={index <= 0} display={{ base: 'none', md: 'inline-flex' }} />
          <IconButton aria-label="Next group" icon={<ChevronRight size={18} />} size="sm" isRound
            position="absolute" right="-6px" top="80px" bg="rgba(10,20,32,0.85)" color="white" _hover={{ bg: 'rgba(139,92,246,0.8)' }}
            onClick={() => go(1)} isDisabled={index >= groups.length - 1} display={{ base: 'none', md: 'inline-flex' }} />
          <HStack justify="center" spacing={1.5} mt={2}>
            {groups.map((g, i) => (
              <Box key={g.id} w={i === index ? '18px' : '6px'} h="6px" borderRadius="full" transition="width 0.2s"
                bg={i === index ? '#a78bfa' : 'whiteAlpha.300'} cursor="pointer"
                onClick={() => { const el = ref.current; if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' }); }} />
            ))}
          </HStack>
        </>
      )}
    </Box>
  );
};

export default GroupCarousel;
